"use client";

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  ReactNode,
} from "react";
import { ProductSchema } from "@/components/products-select/product-schema";
import { FileSchemaType, AiDiscoveryFunnelContext } from "@jurni/schemas";
import { randomString } from "@/utils/utils";
import type {
  BrainAttachment,
  BrainEffort,
  BrainModel,
} from "@jurni/schemas/brain/index";

export interface InitialPromptData {
  autoSubmit: boolean;
  prompt?: string;
  attachments?: BrainAttachment[];
  model?: BrainModel;
  effort?: BrainEffort;
  products?: ProductSchema[];
  files?: FileSchemaType[];
  aiDiscoveryConfigId?: number;
  aiDiscoveryFunnelContext?: AiDiscoveryFunnelContext;
  adMetaId?: string;
}

interface AIGlobalContextType {
  initialPromptData: InitialPromptData | null;
  setInitialPromptData: (data: InitialPromptData | null) => void;
  globalThreadId?: string;
  setGlobalThreadId: (threadId: string) => void;
  generateThreadId: () => string;
  isHistoryPanelOpen: boolean;
  setIsHistoryPanelOpen: (isOpen: boolean) => void;
  /** Navigates to the Agent inside a view transition that morphs the chat box into place. */
  startAgentHandoff: (navigate: () => void) => void;
  /** Called by the Agent once it shows the handed-over prompt, so the transition can animate. */
  completeAgentHandoff: () => void;
}

// The browser freezes the old page until the Agent is ready; never hold it longer than this.
// Browsers abort a view transition whose update takes about 4s, so it stays below that.
const AGENT_HANDOFF_MAX_WAIT_MS = 2500;

const AIGlobalContext = createContext<AIGlobalContextType | undefined>(
  undefined,
);

export function AIGlobalProvider({ children }: { children: ReactNode }) {
  const [initialPromptData, setInitialPromptData] =
    useState<InitialPromptData | null>(null);
  const [globalThreadId, setGlobalThreadId] = useState<string>();
  const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
  const finishAgentHandoffRef = useRef<(() => void) | null>(null);

  const startAgentHandoff = useCallback((navigate: () => void) => {
    // Without motion the transition would only freeze the page while the Agent loads.
    if (
      !document.startViewTransition ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return navigate();
    let isAgentReady = false;
    const transition = document.startViewTransition(
      () =>
        new Promise<void>((resolve) => {
          finishAgentHandoffRef.current = () => {
            isAgentReady = true;
            resolve();
          };
          window.setTimeout(resolve, AGENT_HANDOFF_MAX_WAIT_MS);
          navigate();
        }),
    );
    // If the Agent page wasn't on screen in time, morphing into its half-rendered
    // state makes the composer jump, so switch without animating instead.
    void transition.updateCallbackDone.then(() => {
      finishAgentHandoffRef.current = null;
      if (!isAgentReady) transition.skipTransition();
    });
  }, []);

  const completeAgentHandoff = useCallback(() => {
    finishAgentHandoffRef.current?.();
  }, []);

  return (
    <AIGlobalContext.Provider
      value={{
        initialPromptData,
        setInitialPromptData,
        globalThreadId,
        setGlobalThreadId,
        generateThreadId: randomString,
        isHistoryPanelOpen,
        setIsHistoryPanelOpen,
        startAgentHandoff,
        completeAgentHandoff,
      }}
    >
      {children}
    </AIGlobalContext.Provider>
  );
}

export function useAIGlobal() {
  const context = useContext(AIGlobalContext);
  if (context === undefined) {
    throw new Error("useAIGlobal must be used within an AIGlobalProvider");
  }
  return context;
}
